// LifeCharger Effect (Ultra-Premium Eldritch/Steampunk Aura)
// Overhauled: 
// 1. Genuine multi-layer SVG heart (Gear, Armor, Core)
// 2. High-fidelity tentacles (40+ segments, smooth math, perfectly aligned bone hooks)
// 3. Dramatic violent nova finale

const CONFIG = {
    scale: 1.35, // 10% smaller overall (was 1.5)
    emergenceDuration: 700,
    materializeDuration: 600,
    surgeDuration: 1800,
    retractionDuration: 800,
    hoverSpeed: 2.5,
    hoverAmplitude: 12,
    heartbeatBPM: 100, // Slower pulsation (was 160)
    tentacleCount: 12, // slightly more tentacles since they vary in size now
    essenceCount: 80,
    paths: {
        gear: '/assets/effects/life_charger/gear.svg',
        armor: '/assets/effects/life_charger/armor.svg',
        core: '/assets/effects/life_charger/core.svg'
    }
};

const spriteCache: Record<string, HTMLImageElement> = {};
const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image();
        img.src = src;
        spriteCache[src] = img;
    }
    return spriteCache[src];
};
// Preload
getSprite(CONFIG.paths.gear);
getSprite(CONFIG.paths.armor);
getSprite(CONFIG.paths.core);

// ==========================================
// PROCEDURAL TENTACLES (HIGH-FIDELITY, 40 SEGMENTS)
// ==========================================

class Tentacle {
    segments: number;
    length: number;
    baseRad: number;
    angleOffset: number;
    points: { x: number, y: number, angle: number }[];
    writhingSpeed: number;
    phaseX: number;
    phaseY: number;
    flipSuckers: boolean;

    constructor(angleOffset: number, length: number = 280, segments: number = 40) {
        this.angleOffset = angleOffset;

        // Randomize length and thickness per tentacle
        const sizeVariant = 0.5 + Math.random() * 0.8; // 0.5x to 1.3x size

        this.length = length * sizeVariant;
        this.segments = segments;
        this.baseRad = 55 * Math.max(0.6, sizeVariant);
        this.points = [];
        this.writhingSpeed = (0.003 + Math.random() * 0.002) * (1.5 - sizeVariant * 0.5); // smaller = faster
        this.phaseX = Math.random() * Math.PI * 2;
        this.phaseY = Math.random() * Math.PI * 2;
        this.flipSuckers = Math.random() > 0.5;

        for (let i = 0; i <= this.segments; i++) {
            this.points.push({ x: 0, y: 0, angle: 0 });
        }
    }

    updateAndDraw(ctx: CanvasRenderingContext2D, t: number, progress: number, isRetracting: boolean) {
        const currentLength = this.length * progress;
        const segLen = currentLength / this.segments;

        this.points[0].x = 0;
        this.points[0].y = 0;

        for (let i = 1; i <= this.segments; i++) {
            const prev = this.points[i - 1];
            const current = this.points[i];

            let targetAngle = this.angleOffset;
            const writeFactor = Math.pow(i / this.segments, 1.2);
            const frenzy = isRetracting ? 4 : 1.2;

            targetAngle += Math.sin(t * this.writhingSpeed * frenzy + this.phaseX + i * 0.15) * 1.5 * writeFactor;
            targetAngle += Math.cos(t * this.writhingSpeed * 0.8 * frenzy + this.phaseY - i * 0.05) * 1.0 * writeFactor;

            current.angle = targetAngle;
            current.x = prev.x + Math.cos(targetAngle) * segLen;
            current.y = prev.y + Math.sin(targetAngle) * segLen;
        }

        ctx.save();
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // 1. Shadow/Glow Base
        ctx.beginPath();
        for (let i = 0; i < this.segments; i++) {
            const p = this.points[i];
            if (i === 0) ctx.moveTo(p.x, p.y);
            else ctx.lineTo(p.x, p.y);
        }
        ctx.strokeStyle = 'rgba(10, 0, 20, 0.6)';
        ctx.shadowColor = '#4a004a';
        ctx.shadowBlur = 25;
        ctx.lineWidth = this.baseRad * progress + 8;
        ctx.stroke();

        // 2. Fleshy Metallic Main Body (Polygonal Tube)
        ctx.beginPath();
        ctx.shadowBlur = 0;
        let pLeft = [], pRight = [];
        for (let i = 0; i < this.segments; i++) {
            const p = this.points[i];
            const pNext = this.points[i + 1] || p;
            const angle = Math.atan2(pNext.y - p.y, pNext.x - p.x);
            const taper = Math.pow(1 - i / this.segments, 2.5); // Stronger taper!
            const thickness = this.baseRad * taper * progress;

            pLeft.push({ x: p.x + Math.cos(angle - Math.PI / 2) * thickness, y: p.y + Math.sin(angle - Math.PI / 2) * thickness });
            pRight.push({ x: p.x + Math.cos(angle + Math.PI / 2) * thickness, y: p.y + Math.sin(angle + Math.PI / 2) * thickness });
        }

        ctx.moveTo(pLeft[0].x, pLeft[0].y);
        for (let i = 1; i < pLeft.length; i++) ctx.lineTo(pLeft[i].x, pLeft[i].y);
        ctx.lineTo(this.points[this.segments].x, this.points[this.segments].y);
        for (let i = pRight.length - 1; i >= 0; i--) ctx.lineTo(pRight[i].x, pRight[i].y);
        ctx.closePath();

        const tentacleGrad = ctx.createLinearGradient(-100, -100, 100, 100);
        tentacleGrad.addColorStop(0, '#2b1035');
        tentacleGrad.addColorStop(1, '#541f4d');
        ctx.fillStyle = tentacleGrad;
        ctx.fill();

        ctx.strokeStyle = '#220022';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // 3. Slimy Specular Highlight (Inner Curve offset)
        ctx.beginPath();
        for (let i = 0; i < this.segments * 0.9; i++) {
            const p = this.points[i];
            const pNext = this.points[i + 1] || p;
            const angle = Math.atan2(pNext.y - p.y, pNext.x - p.x);
            const taper = Math.pow(1 - i / this.segments, 2.5);
            const offsetDist = this.baseRad * taper * progress * 0.4;
            const hx = p.x - Math.cos(angle + Math.PI / 2) * offsetDist;
            const hy = p.y - Math.sin(angle + Math.PI / 2) * offsetDist;

            if (i === 0) ctx.moveTo(hx, hy);
            else ctx.lineTo(hx, hy);
        }
        ctx.strokeStyle = 'rgba(255, 180, 220, 0.4)';
        ctx.lineWidth = 3;
        ctx.stroke();

        // 4. Suckers (Drawn AFTER the main body so they sit on top)
        const suckerDir = this.flipSuckers ? 1 : -1;
        for (let i = 2; i < this.segments - 3; i += 4) { // Spread out more
            const p = this.points[i];
            const pNext = this.points[i + 1];
            const angle = Math.atan2(pNext.y - p.y, pNext.x - p.x);
            const taper = Math.pow(1 - i / this.segments, 2.5);
            const segmentThick = this.baseRad * taper * progress;
            const suckerSize = segmentThick * 0.45;
            if (suckerSize < 2.0) continue;

            const sx = p.x + Math.cos(angle + (Math.PI / 2) * suckerDir) * (segmentThick * 0.85); // Pushed out slightly
            const sy = p.y + Math.sin(angle + (Math.PI / 2) * suckerDir) * (segmentThick * 0.85);

            // Base bulb (fleshy mound blending into tentacle)
            ctx.beginPath();
            ctx.ellipse(sx, sy, suckerSize * 1.2, suckerSize * 0.8, angle + (Math.PI / 2) * suckerDir, 0, Math.PI * 2);
            ctx.fillStyle = '#6e1f52';
            ctx.fill();

            // Sucker rim
            ctx.beginPath();
            ctx.ellipse(sx + Math.cos(angle + (Math.PI / 2) * suckerDir) * suckerSize * 0.2,
                sy + Math.sin(angle + (Math.PI / 2) * suckerDir) * suckerSize * 0.2,
                suckerSize * 0.8, suckerSize * 0.5, angle + (Math.PI / 2) * suckerDir, 0, Math.PI * 2);
            ctx.fillStyle = '#9e2d76';
            ctx.fill();
            ctx.strokeStyle = '#360925';
            ctx.lineWidth = 1;
            ctx.stroke();

            // Inner glowing maw
            ctx.beginPath();
            ctx.ellipse(sx + Math.cos(angle + (Math.PI / 2) * suckerDir) * suckerSize * 0.3,
                sy + Math.sin(angle + (Math.PI / 2) * suckerDir) * suckerSize * 0.3,
                suckerSize * 0.4, suckerSize * 0.2, angle + (Math.PI / 2) * suckerDir, 0, Math.PI * 2);
            ctx.fillStyle = '#ff00aa';
            ctx.shadowColor = '#ff00aa';
            ctx.shadowBlur = 10;
            ctx.fill();
            ctx.shadowBlur = 0;
        }

        ctx.restore();
    }
}


// ==========================================
// PARTICLES & TREE VEINS
// ==========================================

class EssenceMote {
    x: number; y: number;
    startX: number; startY: number;
    delay: number; duration: number;
    lifeTime: number = 0;
    size: number;
    color: string;

    constructor() {
        const angle = Math.random() * Math.PI * 2;
        const dist = 400 + Math.random() * 400;
        this.startX = Math.cos(angle) * dist;
        this.startY = Math.sin(angle) * dist;
        this.x = this.startX;
        this.y = this.startY;

        this.delay = Math.random() * 600;
        this.duration = 400 + Math.random() * 300;
        this.size = 3 + Math.random() * 6;

        const colors = ['#00ffff', '#ff66ff', '#ffffff'];
        this.color = colors[Math.floor(Math.random() * colors.length)];
    }

    update(dt: number): boolean {
        if (this.delay > 0) {
            this.delay -= dt;
            return true;
        }
        this.lifeTime += dt;
        let progress = this.lifeTime / this.duration;
        if (progress >= 1) progress = 1;

        const ease = Math.pow(progress, 4);

        this.x = this.startX + (0 - this.startX) * ease;
        this.y = this.startY + (0 - this.startY) * ease;

        const swirlAngle = progress * Math.PI * 4;
        const swirlRadius = (1 - progress) * 80;
        this.x += Math.cos(swirlAngle) * swirlRadius;
        this.y += Math.sin(swirlAngle) * swirlRadius;

        return progress < 1;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.delay > 0) return;
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.shadowColor = this.color;
        ctx.shadowBlur = 15;
        ctx.fill();

        ctx.beginPath();
        ctx.moveTo(this.x, this.y);
        ctx.lineTo(this.x - (this.x - this.startX) * 0.15, this.y - (this.y - this.startY) * 0.15);
        ctx.strokeStyle = this.color;
        ctx.lineWidth = this.size * 0.6;
        ctx.stroke();

        ctx.restore();
    }
}

class HealSpark {
    x: number; y: number;
    vx: number; vy: number;
    life: number; size: number;
    color: string;

    constructor() {
        this.x = 0; this.y = 0;
        const speed = 15 + Math.random() * 25;
        const angle = Math.random() * Math.PI * 2;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.life = 1.0;
        this.size = 6 + Math.random() * 8;
        this.color = Math.random() > 0.5 ? '#ffffff' : '#00ffff';
    }
    update(dt: number) {
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.90;
        this.vy *= 0.90;
        this.life -= 0.015;
    }
    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        ctx.save();
        ctx.globalAlpha = Math.max(0, this.life);
        ctx.globalCompositeOperation = 'screen';
        ctx.translate(this.x, this.y);
        ctx.rotate(this.life * 15);
        ctx.fillStyle = this.color;
        ctx.shadowColor = this.color;
        ctx.shadowBlur = 20;

        ctx.fillRect(-this.size, -this.size / 3, this.size * 2, this.size * 0.6);
        ctx.fillRect(-this.size / 3, -this.size, this.size * 0.6, this.size * 2);
        ctx.restore();
    }
}

// Tree structure for the pulsing heart
type Branch = {
    startX: number; startY: number;
    endX: number; endY: number;
    length: number;
    angle: number;
    delay: number; // progress (0 to 1) at which this branch starts growing
    duration: number; // amount of progress it takes to fully grow
    children: Branch[];
    thickness: number;
};

function generateTree(startX: number, startY: number, length: number, angle: number, depth: number, delay: number, duration: number): Branch {
    const endX = startX + Math.cos(angle) * length;
    const endY = startY + Math.sin(angle) * length;

    const branch: Branch = {
        startX, startY, endX, endY, length, angle, delay, duration,
        children: [],
        thickness: (depth + 1) * 1.5
    };

    if (depth > 0) {
        const numChildren = Math.floor(Math.random() * 2) + 2; // 2 or 3
        const childDelay = delay + duration * 0.5; // Children start growing when parent is half done
        const childDuration = (1.0 - childDelay) / (depth * 0.8); // Ensure they finish by 1.0

        for (let i = 0; i < numChildren; i++) {
            const spread = Math.PI / 2.5; // Very wide spread
            const newAngle = angle - spread / 2 + (Math.random() * spread);
            const newLength = length * (0.6 + Math.random() * 0.3);
            branch.children.push(generateTree(endX, endY, newLength, newAngle, depth - 1, childDelay, childDuration));
        }
    }
    return branch;
}

function drawTree(ctx: CanvasRenderingContext2D, branch: Branch, progress: number) {
    if (progress < branch.delay) return;

    // Once the entire tree passes its duration, it might still draw. We can clip that out or let it draw instantly.
    const rawProgress = (progress - branch.delay) / branch.duration;
    const branchProgress = Math.min(1.0, rawProgress);

    // Performance optimization: DO NOT apply expensive shadow blur repeatedly for already fully-drawn huge trees
    // Only apply blur if this branch or its children are still actively growing/morphing OR periodically per pulse
    // To be perfectly safe, we'll draw it without shadow Blur but slightly thicker glowing lines instead

    const easeProgress = 1 - Math.pow(1 - branchProgress, 3);

    const dx = branch.endX - branch.startX;
    const dy = branch.endY - branch.startY;

    const currX = branch.startX + dx * easeProgress;
    const currY = branch.startY + dy * easeProgress;

    // Draw base line
    ctx.beginPath();
    ctx.moveTo(branch.startX, branch.startY);
    ctx.lineTo(currX, currY);
    ctx.strokeStyle = '#ffb3e6';
    ctx.lineWidth = branch.thickness;
    ctx.stroke();

    // Draw bright core
    ctx.beginPath();
    ctx.moveTo(branch.startX, branch.startY);
    ctx.lineTo(currX, currY);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = branch.thickness * 0.4;
    ctx.stroke();

    // recurse
    // Only process children if this branch has started extending enough
    if (branchProgress > 0.1) {
        for (const child of branch.children) {
            drawTree(ctx, child, progress);
        }
    }
}


// ==========================================
// EFFECT STATE & RENDERER
// ==========================================

export type LifeChargerEffect = {
    kind: 'lifeCharger'
    t: number
    tx: number; ty: number
    data?: {
        phase: 'EMERGENCE' | 'MATERIALIZING' | 'SURGE' | 'SHATTER' | 'RETRACTION'
        tentacles: Tentacle[]
        motes: EssenceMote[]
        sparks: HealSpark[]
        novaRadius: number
        novaOpacity: number
        treeRoot: Branch
    }
}

export const createLifeChargerEffect = (tx: number, ty: number): LifeChargerEffect => ({
    kind: 'lifeCharger', t: 0, tx, ty
});

export const renderLifeChargerEffect = (
    eff: LifeChargerEffect,
    ctx: CanvasRenderingContext2D
): boolean => {
    if (!eff.data) {
        const tentacles: Tentacle[] = [];
        for (let i = 0; i < CONFIG.tentacleCount; i++) {
            const angle = (i / CONFIG.tentacleCount) * Math.PI * 2 + (Math.random() * 0.4 - 0.2);
            const length = 260 + Math.random() * 140;
            tentacles.push(new Tentacle(angle, length, 40));
        }

        const motes: EssenceMote[] = [];
        for (let i = 0; i < CONFIG.essenceCount; i++) {
            motes.push(new EssenceMote());
        }

        // Generate the bio-tree that grows bottom upwards
        // Start from bottom (-py) growing upwards (-Math.PI / 2)
        const treeRoot = generateTree(0, 70, 45, -Math.PI / 2, 4, 0, 0.4);

        eff.data = {
            phase: 'EMERGENCE',
            tentacles,
            motes,
            sparks: [],
            novaRadius: 0,
            novaOpacity: 0,
            treeRoot
        };
    }

    const d = eff.data;
    const dt = 16.7;
    const ShatterDuration = 450;

    if (eff.t < CONFIG.emergenceDuration) {
        d.phase = 'EMERGENCE';
    } else if (eff.t < CONFIG.emergenceDuration + CONFIG.materializeDuration) {
        d.phase = 'MATERIALIZING';
    } else if (eff.t < CONFIG.emergenceDuration + CONFIG.materializeDuration + CONFIG.surgeDuration) {
        d.phase = 'SURGE';
    } else if (eff.t < CONFIG.emergenceDuration + CONFIG.materializeDuration + CONFIG.surgeDuration + ShatterDuration) {
        d.phase = 'SHATTER';
    } else {
        d.phase = 'RETRACTION';
    }

    ctx.save();

    let shakeX = 0;
    let shakeY = 0;
    if (d.phase === 'SURGE') {
        const surgeLocalT = eff.t - (CONFIG.emergenceDuration + CONFIG.materializeDuration);
        if (surgeLocalT > CONFIG.surgeDuration - 500) {
            const intensity = (surgeLocalT - (CONFIG.surgeDuration - 500)) / 500;
            shakeX = (Math.random() - 0.5) * 40 * intensity;
            shakeY = (Math.random() - 0.5) * 40 * intensity;
        }
    }

    ctx.translate(eff.tx + shakeX, eff.ty + shakeY);

    // ==========================================
    // RENDER: TENTACLES
    // ==========================================
    let tentacleProgress = 0;
    if (d.phase === 'EMERGENCE') {
        const p = eff.t / CONFIG.emergenceDuration;
        tentacleProgress = 1 - Math.pow(1 - p, 4);
    } else if (d.phase === 'RETRACTION' || d.phase === 'SHATTER') {
        const totalTillRetract = CONFIG.emergenceDuration + CONFIG.materializeDuration + CONFIG.surgeDuration;
        const localT = eff.t - totalTillRetract;
        const p = localT / (CONFIG.retractionDuration + ShatterDuration);
        tentacleProgress = 1 - Math.pow(p, 3);
        if (tentacleProgress < 0) tentacleProgress = 0;
    } else {
        tentacleProgress = 1.0;
    }

    d.tentacles.forEach(tentacle => {
        tentacle.updateAndDraw(ctx, eff.t, tentacleProgress, d.phase === 'RETRACTION' || d.phase === 'SHATTER');
    });

    // ==========================================
    // RENDER: SVG HEART SYSTEM & TREE
    // ==========================================
    let heartScale = 0;
    let heartAlpha = 0;

    let treeProgress = 0; // Tree growth progress

    if (d.phase === 'EMERGENCE') {
        // Init
    } else if (d.phase === 'MATERIALIZING') {
        const localT = eff.t - CONFIG.emergenceDuration;
        const p = localT / CONFIG.materializeDuration;
        const c4 = (2 * Math.PI) / 3;
        heartScale = p === 1 ? 1 : 1 - Math.pow(2, -10 * p) * Math.sin((p * 10 - 0.75) * c4);
        heartAlpha = Math.min(1, p * 2);
        treeProgress = p; // Grow tree as heart materializes
    } else if (d.phase === 'SURGE') {
        heartScale = 1.0;
        heartAlpha = 1.0;
        treeProgress = 1.0;
    } else if (d.phase === 'SHATTER' || d.phase === 'RETRACTION') {
        const localT = eff.t - (CONFIG.emergenceDuration + CONFIG.materializeDuration + CONFIG.surgeDuration);
        const p = localT / ShatterDuration;
        heartScale = 1.0 + p * 0.6;
        heartAlpha = 1 - p;
        if (heartAlpha < 0) heartAlpha = 0;
        treeProgress = heartAlpha; // Shrink/fade tree
    }

    ctx.save();
    if (heartAlpha > 0 && heartScale > 0) {
        const hoverOffset = Math.sin((eff.t / 1000) * CONFIG.hoverSpeed) * CONFIG.hoverAmplitude;
        ctx.translate(0, hoverOffset);

        let scaleMultiplier = 1.0;
        let coreIntensity = 1.0;

        if (d.phase === 'SURGE') {
            const surgeLocalT = eff.t - (CONFIG.emergenceDuration + CONFIG.materializeDuration);
            const beatInterval = (60 / CONFIG.heartbeatBPM) * 1000;
            const beatProgress = (surgeLocalT % beatInterval) / beatInterval;

            if (beatProgress < 0.15) {
                scaleMultiplier = 1.0 + Math.sin((beatProgress / 0.15) * Math.PI) * 0.3; // High bump
                coreIntensity = 1.8;
            } else if (beatProgress > 0.25 && beatProgress < 0.5) {
                scaleMultiplier = 1.0 + Math.sin(((beatProgress - 0.25) / 0.25) * Math.PI) * 0.15;
                coreIntensity = 1.4;
            }

            if (surgeLocalT > CONFIG.surgeDuration - 300) {
                const swell = (surgeLocalT - (CONFIG.surgeDuration - 300)) / 300;
                scaleMultiplier += swell * 0.5;
                coreIntensity += swell * 2.0;
            }
        } else if (d.phase === 'SHATTER') {
            coreIntensity = 4.0;
        }

        ctx.scale(CONFIG.scale * heartScale, CONFIG.scale * heartScale);
        ctx.globalAlpha = heartAlpha;

        const renderLayer = (key: 'gear' | 'armor' | 'core', rot: number, s: number, glowColor: string, blur: number) => {
            const img = getSprite(CONFIG.paths[key]);
            if (img.complete && img.naturalWidth > 0) {
                ctx.save();
                ctx.rotate(rot);
                ctx.scale(s, s);
                if (blur > 0) {
                    ctx.shadowColor = glowColor;
                    ctx.shadowBlur = blur;
                }
                const w = img.naturalWidth;
                const h = img.naturalHeight;
                ctx.drawImage(img, -w / 2, -h / 2, w, h);
                ctx.restore();
            }
        };

        // 1. Gears (Slowly rotating back layer)
        renderLayer('gear', eff.t * 0.0005, 1.0, '#000', 0);

        // 2. Armor (Static casing)
        renderLayer('armor', 0, 1.0, '#000', 10);

        // 3. Core (Beating biological heart)
        ctx.save();
        ctx.globalCompositeOperation = coreIntensity > 1.5 ? 'screen' : 'source-over';
        renderLayer('core', 0, scaleMultiplier, '#ff00aa', (coreIntensity - 1) * 30);

        // 4. Draw the Tree growing over the core
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.scale(scaleMultiplier, scaleMultiplier); // scale tree with heart
        // Outer glow applied globally for the tree (major performance gain vs blurring every single twig)
        ctx.shadowColor = '#ff00aa';
        ctx.shadowBlur = 15;
        drawTree(ctx, d.treeRoot, treeProgress);
        ctx.restore();

        ctx.restore();

        // Motes rendering (Inflow)
        if (d.phase === 'SURGE') {
            d.motes = d.motes.filter(mote => mote.update(dt));
            d.motes.forEach(mote => mote.draw(ctx));
        }
    }
    ctx.restore();

    // ==========================================
    // RENDER: NOVA SHOCKWAVE CLIMAX & SHATTER
    // ==========================================
    if (d.phase === 'SURGE') {
        const surgeLocalT = eff.t - (CONFIG.emergenceDuration + CONFIG.materializeDuration);
        if (surgeLocalT > CONFIG.surgeDuration - 120) {
            ctx.save();
            ctx.globalCompositeOperation = 'screen';
            ctx.beginPath();
            ctx.arc(0, 0, 150, 0, Math.PI * 2);
            ctx.fillStyle = 'white';
            ctx.fill();
            ctx.restore();
        }
    } else if (d.phase === 'SHATTER' || d.phase === 'RETRACTION') {
        const shatterLocalT = eff.t - (CONFIG.emergenceDuration + CONFIG.materializeDuration + CONFIG.surgeDuration);

        if (d.phase === 'SHATTER' && d.sparks.length === 0) {
            for (let k = 0; k < 60; k++) d.sparks.push(new HealSpark());
        }

        const novaTime = shatterLocalT;
        if (novaTime < 700) {
            const progress = novaTime / 700;
            const easeOut = 1 - Math.pow(1 - progress, 3);

            ctx.save();
            ctx.globalCompositeOperation = 'screen';
            ctx.globalAlpha = 1 - progress;

            ctx.beginPath();
            ctx.arc(0, 0, easeOut * 450, 0, Math.PI * 2);
            const rGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, easeOut * 450);
            rGrad.addColorStop(0, 'rgba(255, 255, 255, 1)');
            rGrad.addColorStop(0.3, 'rgba(0, 255, 255, 0.9)');
            rGrad.addColorStop(1, 'rgba(0, 50, 255, 0)');
            ctx.fillStyle = rGrad;
            ctx.fill();

            ctx.beginPath();
            ctx.arc(0, 0, easeOut * 900, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(0, 255, 255, ${1 - progress})`;
            ctx.lineWidth = 50 * (1 - progress);
            ctx.stroke();

            ctx.beginPath();
            for (let i = 0; i < 20; i++) {
                const a = (i / 20) * Math.PI * 2 + (novaTime * 0.001);
                ctx.moveTo(Math.cos(a) * (easeOut * 120), Math.sin(a) * (easeOut * 120));
                ctx.lineTo(Math.cos(a) * (easeOut * 1200), Math.sin(a) * (easeOut * 1200));
            }
            ctx.strokeStyle = `rgba(255, 200, 255, ${1 - progress})`;
            ctx.lineWidth = 18;
            ctx.stroke();

            ctx.restore();
        }

        d.sparks.forEach(s => {
            s.update(dt);
            s.draw(ctx);
        });
    }

    ctx.restore();

    const totalTime = CONFIG.emergenceDuration + CONFIG.materializeDuration + CONFIG.surgeDuration + ShatterDuration + CONFIG.retractionDuration;
    if (eff.t > totalTime) {
        return false;
    }

    return true;
};
